import type { WeaponDef } from "../data/weapons";

// The mutable player stat block. Upgrades mutate these; weapon systems read
// them. Multipliers default to 1, additive bonuses to 0.
export interface PlayerStats {
  // Movement & survivability
  moveSpeedBase: number;
  moveSpeedMult: number;
  maxIntegrityBase: number;
  maxIntegrityAdd: number;
  regenPerSec: number;
  /** Passive node self-heal (stability/sec) for ONLINE nodes — upgrade-only. */
  nodeRegenPerSec: number;

  // Pickups
  pickupRadiusBase: number;
  pickupRadiusMult: number;
  healOnPickup: number;

  // Weapon modifiers (applied on top of each weapon's own def)
  damageMult: number;
  attackSpeed: number; // multiplies firing rate (cooldown / attackSpeed)
  rangeMult: number;
  projectileSpeedMult: number;
  critChanceAdd: number;
  countAdd: number; // extra projectiles / zap targets
  pierceAdd: number;
  chainAdd: number;
  knockbackMult: number;
}

export function defaultStats(): PlayerStats {
  return {
    moveSpeedBase: 235,
    moveSpeedMult: 1,
    maxIntegrityBase: 100,
    maxIntegrityAdd: 0,
    regenPerSec: 0,
    nodeRegenPerSec: 0,

    pickupRadiusBase: 70,
    pickupRadiusMult: 1,
    healOnPickup: 0,

    damageMult: 1,
    attackSpeed: 1,
    rangeMult: 1,
    projectileSpeedMult: 1,
    critChanceAdd: 0,
    countAdd: 0,
    pierceAdd: 0,
    chainAdd: 0,
    knockbackMult: 1,
  };
}

export function maxIntegrity(s: PlayerStats): number {
  return s.maxIntegrityBase + s.maxIntegrityAdd;
}

export function moveSpeed(s: PlayerStats): number {
  return s.moveSpeedBase * s.moveSpeedMult;
}

export function pickupRadius(s: PlayerStats): number {
  return s.pickupRadiusBase * s.pickupRadiusMult;
}

// Resolved per-weapon values after applying player stats.
export interface ResolvedWeapon {
  cooldown: number;
  damage: number;
  range: number;
  count: number;
  projectileSpeed: number;
  pierce: number;
  critChance: number;
  critMult: number;
  knockback: number;
}

export function resolveWeapon(w: WeaponDef, s: PlayerStats): ResolvedWeapon {
  return {
    cooldown: w.cooldown / s.attackSpeed,
    damage: w.damage * s.damageMult,
    range: w.range * s.rangeMult,
    count: w.count + s.countAdd,
    projectileSpeed: w.projectileSpeed * s.projectileSpeedMult,
    pierce: w.pierce + s.pierceAdd,
    critChance: w.critChance + s.critChanceAdd,
    critMult: w.critMult,
    knockback: w.knockback * s.knockbackMult,
  };
}
